import { firestoreService } from './firestore';
import { defaultListingType, normaliseListingType } from '../utils/listingTypes';
import { normaliseAssetGrids, normaliseGridAccessLevel } from '../utils/assetGrids';
import { normaliseCustomDomain } from '../utils/shareLinks';
import { auth, db } from './firebase';
import {
  collection,
  deleteField,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from 'firebase/firestore';

const defaultLink = {
  title: 'Untitled Sharabl link',
  category: 'album',
  description: '',
  renderFont: 'Inter',
  backgroundColor: '#f7f9fb',
  headerBackgroundColor: '#ffffff',
  assetCardBackgroundColor: '#ffffff',
  contactTel: '',
  contactWhatsApp: '',
  contactEmail: '',
  price: '',
  currency: 'ZAR',
  owner: 'Workspace',
  ownerId: '',
  slug: 'untitled-share',
  useCustomDomain: false,
  customDomain: '',
  expiry: '14 days',
  startsAt: '',
  endsAt: '',
  protection: 'Invite only',
  inviteRequestMode: 'request',
  allowDownloads: false,
  allowSharing: false,
  assetFields: {
    title: true,
    description: true,
    price: true,
    quantity: true,
    area: true,
    sizes: false,
    colors: false,
    link: false,
    multipleImages: false,
    enquire: false,
    updates: false,
  },
  layout: 'masonry',
  showHeroSection: true,
  showCreateLinkButton: true,
  publicPageWidth: '6xl',
  galleryColumns: 3,
  assetGrids: [],
  status: 'Draft',
  demoMode: false,
  icon: null,
  logo: null,
  logoHeight: 48,
  ogImage: null,
  assets: [],
  form: {
    enabled: false,
    title: 'Share your details',
    description: 'Complete this form when you view these assets.',
    buttonLabel: 'Response form',
    submitLabel: 'Submit response',
    fields: [
      { id: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Your name' },
      { id: 'email', label: 'Email', type: 'email', required: true, placeholder: 'you@example.com' },
    ],
  },
  itemForms: [],
  adminIds: [],
  managerIds: [],
  collaborators: [],
  checkout: {
    enableRestaurantOrdering: false,
    enableOnlineCheckout: true,
    enablePosMode: false,
    cartButtonLabel: 'Add to cart',
    drawerEyebrow: 'Store checkout',
    drawerTitle: 'Cart',
    emptyCartMessage: 'Add store items to start an order.',
    customerDetailsLabel: 'Customer details',
    formTitle: 'Send order',
    nameLabel: 'Name',
    emailLabel: 'Email',
    phoneLabel: 'Phone',
    notesLabel: 'Notes',
    addressLabel: 'Delivery address',
    tableNumberLabel: 'Table number',
    deliveryFee: '0',
    enableOfflinePaymentDetails: false,
    bankName: '',
    accountName: '',
    accountNumber: '',
    branchCode: '',
    accountType: '',
    paymentInstructions: '',
    submitLabel: 'Send order',
    successMessage: 'Order sent.',
  },
  views: 0,
  shares: 0,
  viewKeys: [],
  shareKeys: [],
};

const LINK_CONTENT_VERSION = 2;
const LINK_ITEMS_COLLECTION = 'items';
const LINK_GRIDS_COLLECTION = 'grids';
const LINK_ITEM_FORMS_COLLECTION = 'itemForms';
const WRITE_BATCH_LIMIT = 450;

const serialiseListItems = (listItems) => (
  Array.isArray(listItems)
    ? listItems.map((item = {}, index) => ({
      ...item,
      id: item.id || `list-item-${index + 1}`,
      tags: Array.isArray(item.tags) ? item.tags.join(', ') : item.tags || '',
    }))
    : []
);

const serialiseAsset = (asset) => {
  const { file, iconFile, listingLogoFile, ...serialisableAsset } = asset;
  return {
    ...serialisableAsset,
    ...(Array.isArray(serialisableAsset.listItems)
      ? {
        listItems: serialiseListItems(serialisableAsset.listItems),
        size: serialisableAsset.size || `${serialisableAsset.listItems.length} items`,
      }
      : {}),
    images: Array.isArray(serialisableAsset.images)
      ? serialisableAsset.images.map((image) => {
        const { file: imageFile, preview, ...serialisableImage } = image;
        return serialisableImage;
      })
      : [],
  };
};

const removeUndefinedFields = (value) => {
  if (Array.isArray(value)) {
    return value
      .filter((item) => item !== undefined)
      .map(removeUndefinedFields);
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, entryValue]) => entryValue !== undefined)
        .map(([key, entryValue]) => [key, removeUndefinedFields(entryValue)]),
    );
  }

  return value;
};

const serialiseGrid = (grid) => ({
  id: grid.id,
  title: grid.title || '',
  type: grid.type || 'standard',
  accessLevel: normaliseGridAccessLevel(grid.accessLevel),
  category: grid.category || 'album',
  listingType: normaliseListingType(grid.listingType || defaultListingType),
  layout: grid.layout || 'masonry',
  columns: grid.columns || 3,
  assetIds: Array.isArray(grid.assetIds) ? grid.assetIds : [],
  listTitle: grid.listTitle || '',
  listDescription: grid.listDescription || '',
  listImageUrl: grid.listImageUrl || '',
  listTags: Array.isArray(grid.listTags) ? grid.listTags : [],
  listActionLabel: grid.listActionLabel || '',
  listActionUrl: grid.listActionUrl || '',
  listColumns: Array.isArray(grid.listColumns) ? grid.listColumns : [],
  listRows: Array.isArray(grid.listRows) ? grid.listRows : [],
  richContentTitle: grid.richContentTitle || '',
  richContentMarkdown: grid.richContentMarkdown || '',
});

const splitLinkContent = (link = {}) => {
  const {
    assets = [],
    assetGrids = [],
    itemForms = [],
    ...documentData
  } = link;

  return {
    documentData: {
      ...documentData,
      ownerId: link.ownerId || auth.currentUser?.uid || '',
      assetCount: assets.length,
      gridCount: assetGrids.length,
      itemFormCount: itemForms.length,
      contentConfigVersion: LINK_CONTENT_VERSION,
    },
    assets: assets.map(serialiseAsset),
    assetGrids: assetGrids.map(serialiseGrid),
    itemForms,
  };
};

const serialiseLinkDocument = (link = {}, { clearEmbeddedContent = false } = {}) => {
  const { documentData } = splitLinkContent(link);
  return clearEmbeddedContent
    ? {
      ...documentData,
      assets: deleteField(),
      assetGrids: deleteField(),
      itemForms: deleteField(),
    }
    : documentData;
};

const canUseInternalLinkTools = (link) => {
  const uid = auth.currentUser?.uid;
  return Boolean(
    uid &&
    (
      link.ownerId === uid ||
      (Array.isArray(link.adminIds) && link.adminIds.includes(uid)) ||
      (Array.isArray(link.managerIds) && link.managerIds.includes(uid))
    ),
  );
};

const canHydrateProtectedPublicContent = (link) => (
  link?.protection === 'Public link' ||
  canUseInternalLinkTools(link)
);

const isProtectedSharablLink = (link) => ['Invite only', 'Password'].includes(link?.protection);

const canReadGridWithoutLinkCredentials = (link, grid = {}) => {
  const accessLevel = normaliseGridAccessLevel(grid.accessLevel);
  if (accessLevel === 'public') return true;
  if (accessLevel === 'signed-in') return Boolean(auth.currentUser);
  if (accessLevel === 'private' || accessLevel === 'invite-only') return false;
  return link?.protection === 'Public link';
};

const publicGridContentShell = (link) => {
  const grids = normaliseAssetGrids(link).filter((grid) => canReadGridWithoutLinkCredentials(link, grid));
  const visibleAssetIds = new Set(grids.flatMap((grid) => grid.assetIds || []));
  const visibleAssets = (link.assets || []).filter((asset) => visibleAssetIds.has(asset.id));
  const visibleGridIds = new Set(grids.map((grid) => grid.id));
  const formBelongsToVisibleGrid = !link.form?.gridId || visibleGridIds.has(link.form.gridId);

  return {
    ...link,
    isProtectedContentShell: true,
    assets: visibleAssets,
    assetGrids: grids,
    itemForms: [],
    form: formBelongsToVisibleGrid
      ? link.form
      : {
        ...(link.form || {}),
        enabled: false,
        fields: [],
      },
  };
};

const toPublicCredentialShell = (link, includeProtectedContent = false) => {
  const scopedLink = canUseInternalLinkTools(link) || link?.demoMode ? link : { ...link, itemForms: [] };
  if (includeProtectedContent || canHydrateProtectedPublicContent(scopedLink)) return scopedLink;
  return publicGridContentShell(scopedLink);
};

const hostsMatch = (configuredHost, requestHost) => {
  const configured = normaliseCustomDomain(configuredHost);
  const requested = normaliseCustomDomain(requestHost);
  if (!configured || !requested) return false;

  return configured === requested ||
    configured === `www.${requested}` ||
    `www.${configured}` === requested;
};

const serialiseDocument = (snapshot) => {
  const data = snapshot.data();
  return {
    id: snapshot.id,
    ...data,
    createdAt: data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : data.createdAt,
    updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate().toISOString() : data.updatedAt,
  };
};

const orderedSubcollection = async (linkId, collectionName) => {
  const collectionRef = collection(db, 'sharabl-links', linkId, collectionName);
  const snapshot = await getDocs(query(collectionRef, orderBy('order', 'asc')));
  return snapshot.docs.map((itemSnapshot) => {
    const { order, ...data } = itemSnapshot.data();
    return {
      id: data.id || itemSnapshot.id,
      ...data,
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : data.createdAt,
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate().toISOString() : data.updatedAt,
    };
  });
};

const safeOrderedSubcollection = async (linkId, collectionName) => {
  try {
    return await orderedSubcollection(linkId, collectionName);
  } catch (error) {
    if (error?.code !== 'permission-denied') {
      throw error;
    }
    return [];
  }
};

const hydrateLinkContent = async (link) => {
  if (!link?.id) return link;

  const canHydrateInternalContent = canUseInternalLinkTools(link) || link?.demoMode === true;
  const [items, grids, itemForms] = await Promise.all([
    safeOrderedSubcollection(link.id, LINK_ITEMS_COLLECTION),
    safeOrderedSubcollection(link.id, LINK_GRIDS_COLLECTION),
    canHydrateInternalContent ? safeOrderedSubcollection(link.id, LINK_ITEM_FORMS_COLLECTION) : Promise.resolve([]),
  ]);

  return {
    ...link,
    assets: items.length ? items : (Array.isArray(link.assets) ? link.assets : []),
    assetGrids: grids.length ? grids : (Array.isArray(link.assetGrids) ? link.assetGrids : []),
    itemForms: canHydrateInternalContent
      ? (itemForms.length ? itemForms : (Array.isArray(link.itemForms) ? link.itemForms : []))
      : [],
  };
};

const chunk = (items, size) => {
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const replaceSubcollection = async (linkId, collectionName, records) => {
  const collectionRef = collection(db, 'sharabl-links', linkId, collectionName);
  const currentSnapshot = await getDocs(collectionRef);
  const nextIds = new Set(records.map((record, index) => record.id || `${collectionName}-${index + 1}`));
  const writes = [
    ...records.map((record, index) => {
      const recordId = record.id || `${collectionName}-${index + 1}`;
      return {
        type: 'set',
        ref: doc(collectionRef, recordId),
        data: {
          ...removeUndefinedFields(record),
          id: recordId,
          order: index,
          updatedAt: serverTimestamp(),
        },
      };
    }),
    ...currentSnapshot.docs.filter((itemSnapshot) => !nextIds.has(itemSnapshot.id)).map((itemSnapshot) => ({
      type: 'delete',
      ref: itemSnapshot.ref,
    })),
  ];

  for (const writesChunk of chunk(writes, WRITE_BATCH_LIMIT)) {
    const batch = writeBatch(db);
    writesChunk.forEach((write) => {
      if (write.type === 'delete') {
        batch.delete(write.ref);
      } else {
        batch.set(write.ref, write.data);
      }
    });
    await batch.commit();
  }
};

const saveLinkContent = async (linkId, link) => {
  const { assets, assetGrids, itemForms } = splitLinkContent(link);
  await Promise.all([
    replaceSubcollection(linkId, LINK_ITEMS_COLLECTION, assets),
    replaceSubcollection(linkId, LINK_GRIDS_COLLECTION, assetGrids),
    replaceSubcollection(linkId, LINK_ITEM_FORMS_COLLECTION, itemForms),
  ]);
};

const deleteSubcollection = async (linkId, collectionName) => {
  const collectionRef = collection(db, 'sharabl-links', linkId, collectionName);
  const snapshot = await getDocs(collectionRef);
  for (const docsChunk of chunk(snapshot.docs, WRITE_BATCH_LIMIT)) {
    const batch = writeBatch(db);
    docsChunk.forEach((itemSnapshot) => batch.delete(itemSnapshot.ref));
    await batch.commit();
  }
};

const listLinkDocuments = async (collectionRef, ...constraints) => {
  try {
    const snapshot = await getDocs(query(collectionRef, ...constraints));
    return snapshot.docs;
  } catch (error) {
    if (error?.code === 'permission-denied' || error?.code === 'failed-precondition') {
      console.warn('Unable to load one Sharabl link role query.', error);
      return [];
    }
    throw error;
  }
};

const canDuplicateLink = (link, { allowAdminOverride = false } = {}) => {
  const uid = auth.currentUser?.uid;
  const role = auth.currentUser?.profile?.role || auth.currentUser?.role;
  return Boolean(
    uid &&
    link &&
    (
      link.ownerId === uid ||
      (Array.isArray(link.adminIds) && link.adminIds.includes(uid)) ||
      (Array.isArray(link.managerIds) && link.managerIds.includes(uid)) ||
      role === 'admin' ||
      allowAdminOverride
    ),
  );
};

const duplicateSlug = (link) => {
  const baseSlug = (link.slug || link.title || defaultLink.slug)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '') || defaultLink.slug;

  return `${baseSlug}-copy-${Date.now().toString(36)}`;
};

export const linksService = {
  async listLinks() {
    const uid = auth.currentUser?.uid || '';
    if (!uid) return [];

    const collectionRef = collection(db, 'sharabl-links');
    const snapshots = await Promise.all([
      listLinkDocuments(collectionRef, where('ownerId', '==', uid)),
      listLinkDocuments(collectionRef, where('adminIds', 'array-contains', uid)),
      listLinkDocuments(collectionRef, where('managerIds', 'array-contains', uid)),
    ]);
    const linksById = new Map();

    snapshots.flat().forEach((linkSnapshot) => {
      linksById.set(linkSnapshot.id, serialiseDocument(linkSnapshot));
    });

    const links = [...linksById.values()].sort((firstLink, secondLink) => (
      new Date(secondLink.updatedAt || 0) - new Date(firstLink.updatedAt || 0)
    ));

    return Promise.all(links.map(hydrateLinkContent));
  },

  async getLink(id) {
    const link = await firestoreService.get('sharabl-links', id);
    return hydrateLinkContent(link);
  },

  async getLinkBySlug(slug, options = {}) {
    if (!slug) return null;
    const collectionRef = collection(db, 'sharabl-links');
    const ownerFilters = auth.currentUser
      ? [where('slug', '==', slug), where('ownerId', '==', auth.currentUser.uid)]
      : [];
    const externallyVisibleProtectionLevels = ['Public link', 'Invite only', 'Password'];

    const ownerSnapshot = ownerFilters.length
      ? await getDocs(query(collectionRef, ...ownerFilters, limit(1)))
      : null;
    let snapshot = ownerSnapshot && !ownerSnapshot.empty ? ownerSnapshot : null;

    if (!snapshot) {
      for (const protection of externallyVisibleProtectionLevels) {
        const protectionSnapshot = await getDocs(query(
          collectionRef,
          where('slug', '==', slug),
          where('protection', '==', protection),
          limit(1),
        ));

        if (!protectionSnapshot.empty) {
          snapshot = protectionSnapshot;
          break;
        }
      }
    }

    if (!snapshot || snapshot.empty) return null;

    const link = serialiseDocument(snapshot.docs[0]);
    const canIncludeContent = options.includeProtectedContent || canHydrateProtectedPublicContent(link);
    const shouldHydrateForGridAccess = canIncludeContent || isProtectedSharablLink(link);
    const hydratedLink = shouldHydrateForGridAccess ? await hydrateLinkContent(link) : link;
    return toPublicCredentialShell(hydratedLink, options.includeProtectedContent);
  },

  async getLinkByCustomDomain(host, options = {}) {
    const normalisedHost = normaliseCustomDomain(host);
    if (!normalisedHost) return null;

    const collectionRef = collection(db, 'sharabl-links');
    const snapshot = await getDocs(query(
      collectionRef,
      where('useCustomDomain', '==', true),
      limit(100),
    ));

    if (snapshot.empty) return null;

    const documents = snapshot.docs.map(serialiseDocument);
    const link = documents.find((document) => hostsMatch(document.customDomain, normalisedHost));
    if (!link) return null;

    const canIncludeContent = options.includeProtectedContent || canHydrateProtectedPublicContent(link);
    const shouldHydrateForGridAccess = canIncludeContent || isProtectedSharablLink(link);
    const hydratedLink = shouldHydrateForGridAccess ? await hydrateLinkContent(link) : link;
    return toPublicCredentialShell(hydratedLink, options.includeProtectedContent);
  },

  async createLink(data = {}) {
    const slug = (data.slug || data.title || defaultLink.slug)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

    const link = { ...defaultLink, ...data, slug };
    const created = await firestoreService.create('sharabl-links', serialiseLinkDocument(link), 'link');
    await saveLinkContent(created.id, link);
    return {
      ...created,
      assets: link.assets || [],
      assetGrids: link.assetGrids || [],
      itemForms: link.itemForms || [],
    };
  },

  async duplicateLink(link, options = {}) {
    if (!canDuplicateLink(link, options)) {
      throw new Error('Only link owners, admins, or managers can duplicate this link.');
    }

    const hydratedLink = await this.getLink(link.id);
    if (!hydratedLink || !canDuplicateLink(hydratedLink, options)) {
      throw new Error('Only link owners, admins, or managers can duplicate this link.');
    }

    const linkData = { ...hydratedLink };
    [
      'id',
      'createdAt',
      'updatedAt',
      'assetCount',
      'gridCount',
      'itemFormCount',
      'contentConfigVersion',
    ].forEach((field) => {
      delete linkData[field];
    });
    const currentUser = auth.currentUser;
    const ownerLabel = currentUser?.displayName || currentUser?.email || linkData.owner || 'Workspace';

    return this.createLink({
      ...linkData,
      title: `${linkData.title || defaultLink.title} copy`,
      slug: duplicateSlug(linkData),
      owner: ownerLabel,
      ownerId: currentUser?.uid || linkData.ownerId || '',
      adminIds: [],
      managerIds: [],
      collaborators: [],
      views: 0,
      shares: 0,
      viewKeys: [],
      shareKeys: [],
      status: 'Draft',
    });
  },

  async updateLink(id, data) {
    const saved = await firestoreService.update('sharabl-links', id, serialiseLinkDocument(data, { clearEmbeddedContent: true }));
    await saveLinkContent(id, data);
    return {
      ...saved,
      assets: (data.assets || []).map(serialiseAsset),
      assetGrids: (data.assetGrids || []).map(serialiseGrid),
      itemForms: data.itemForms || [],
    };
  },

  addAssets(id, assets) {
    return this.getLink(id).then((link) => {
      if (!link) return null;
      return this.updateLink(id, { assets: [...assets, ...(link.assets || [])] });
    });
  },

  async removeLink(id) {
    await Promise.all([
      deleteSubcollection(id, LINK_ITEMS_COLLECTION),
      deleteSubcollection(id, LINK_GRIDS_COLLECTION),
      deleteSubcollection(id, LINK_ITEM_FORMS_COLLECTION),
    ]);
    return firestoreService.remove('sharabl-links', id);
  },
};
